import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Scissors, CheckCircle2, Factory, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import {
  getPublicWorkshopDay,
  togglePublicWorkshopDayCutCheck,
} from "@/lib/api";

const fmtDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(`${iso}T00:00:00`).toLocaleDateString(undefined, {
      weekday: "long",
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
  } catch {
    return iso;
  }
};

const WorkshopDay = () => {
  const { date, token } = useParams();
  const [data, setData] = useState(null);
  // map<quoteId, Set<key>>
  const [checks, setChecks] = useState({});
  // map<quoteId, producedAt|null>
  const [produced, setProduced] = useState({});
  const [status, setStatus] = useState("loading");
  const [errorMsg, setErrorMsg] = useState("");

  // Lookup map: screen-type key → human label (e.g. "sliding_window_plain" → "Sliding Window")
  const typeLabel = useMemo(() => {
    const map = {};
    (data?.screen_types || []).forEach((t) => {
      map[t.key] = t.label;
    });
    return map;
  }, [data]);

  useEffect(() => {
    getPublicWorkshopDay(date, token)
      .then((d) => {
        setData(d);
        const checkMap = {};
        const prodMap = {};
        (d.quotes || []).forEach((q) => {
          checkMap[q.id] = new Set(q.cut_checks || []);
          prodMap[q.id] = q.produced_at || null;
        });
        setChecks(checkMap);
        setProduced(prodMap);
        setStatus("ok");
      })
      .catch((err) => {
        const detail = err?.response?.data?.detail || "Link invalid or expired";
        setErrorMsg(typeof detail === "string" ? detail : "Link invalid");
        setStatus("error");
      });
  }, [date, token]);

  const totals = useMemo(() => {
    let total = 0;
    let done = 0;
    (data?.quotes || []).forEach((q) => {
      (q.rooms || []).forEach((r) => {
        total += (r.cut_pieces || []).length;
      });
      done += (checks[q.id]?.size || 0);
    });
    return { total, done };
  }, [data, checks]);

  const progress = totals.total
    ? Math.round((totals.done / totals.total) * 100)
    : 0;

  const toggle = async (quoteId, key) => {
    const current = checks[quoteId] || new Set();
    const wasDone = current.has(key);
    const nextDone = !wasDone;
    const next = new Set(current);
    if (nextDone) next.add(key);
    else next.delete(key);
    setChecks({ ...checks, [quoteId]: next });
    try {
      const res = await togglePublicWorkshopDayCutCheck(
        date,
        token,
        quoteId,
        key,
        nextDone
      );
      setChecks((c) => ({ ...c, [quoteId]: new Set(res.cut_checks || []) }));
      setProduced((p) => ({ ...p, [quoteId]: res.produced_at || null }));
    } catch {
      setChecks((c) => ({ ...c, [quoteId]: current }));
      toast.error("Couldn't save — check your internet");
    }
  };

  if (status === "loading") {
    return (
      <div
        className="min-h-screen flex items-center justify-center bg-white"
        data-testid="workshop-day-loading"
      >
        <div className="font-mono text-xs text-zinc-400 uppercase tracking-[0.2em]">
          Loading cut day…
        </div>
      </div>
    );
  }

  if (status === "error") {
    return (
      <div
        className="min-h-screen flex items-center justify-center bg-white px-6"
        data-testid="workshop-day-error"
      >
        <div className="max-w-sm text-center">
          <div className="w-12 h-12 mx-auto mb-6 border-2 border-red-500 text-red-500 flex items-center justify-center">
            <Scissors size={20} />
          </div>
          <h1 className="font-display text-3xl font-black tracking-tighter mb-3">
            Link invalid
          </h1>
          <p className="text-sm text-zinc-600 font-mono">{errorMsg}</p>
        </div>
      </div>
    );
  }

  const quotes = data?.quotes || [];

  return (
    <div className="min-h-screen bg-white" data-testid="workshop-day-view">
      {/* Force portrait orientation when this workshop page is printed. */}
      <style>{`@media print { @page { size: A4 portrait; margin: 8mm; } }`}</style>
      <header className="border-b border-zinc-200 sticky top-0 bg-white z-10">
        <div className="max-w-4xl mx-auto p-5 md:p-8">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-10 bg-[#09090b] text-white flex items-center justify-center">
              <Factory size={18} />
            </div>
            <div>
              <div className="font-display text-lg font-black leading-none tracking-tighter">
                WORKSHOP · DAY
              </div>
              <div className="label-xs mt-1">Cut list · {quotes.length} job{quotes.length === 1 ? "" : "s"}</div>
            </div>
          </div>

          <h1 className="font-display text-2xl sm:text-3xl font-black leading-[0.95] tracking-tighter">
            {fmtDate(data.date)}
          </h1>

          <div
            className="mt-5 border border-zinc-200 p-4"
            data-testid="workshop-day-progress"
          >
            <div className="flex items-end justify-between mb-3">
              <div>
                <div className="label-xs">Total progress</div>
                <div className="font-display text-3xl font-black tracking-tighter leading-none mt-1">
                  {totals.done}
                  <span className="text-zinc-300">/{totals.total}</span>
                </div>
              </div>
              <div className="text-right font-mono text-xs text-zinc-500">
                {progress}%
              </div>
            </div>
            <div className="h-1 bg-zinc-100">
              <div
                className="h-full bg-[#09090b] transition-all duration-300"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto p-5 md:p-8 space-y-10">
        {quotes.length === 0 ? (
          <div className="font-mono text-xs text-zinc-400 italic text-center py-10">
            No jobs scheduled for this date.
          </div>
        ) : null}

        {quotes.map((q, qi) => {
          const qChecks = checks[q.id] || new Set();
          const qPieces = (q.rooms || []).reduce(
            (s, r) => s + (r.cut_pieces?.length || 0),
            0
          );
          const qDone = qChecks.size;
          const qProduced = produced[q.id];
          return (
            <section
              key={q.id}
              data-testid={`workshop-day-job-${qi}`}
              className="border border-[#09090b]"
            >
              {/* Job header */}
              <div className="bg-[#09090b] text-white px-5 py-4 flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400">
                    Job {String(qi + 1).padStart(2, "0")} of {quotes.length}
                  </div>
                  <h2 className="font-display text-2xl sm:text-3xl font-black tracking-tighter mt-1">
                    {q.client_name || "(Unnamed)"}
                  </h2>
                  <div className="font-mono text-xs text-zinc-300 mt-1 flex flex-wrap gap-x-3">
                    {q.area ? <span>{q.area}</span> : null}
                    {q.color ? <span>{q.color}</span> : null}
                    <span>
                      {qDone}/{qPieces} pieces
                    </span>
                  </div>
                </div>
                {qProduced ? (
                  <div className="inline-flex items-center gap-1 bg-green-500 text-white px-2 py-1 text-[10px] uppercase tracking-[0.18em] font-semibold flex-none">
                    <CheckCircle2 size={12} /> Done
                  </div>
                ) : null}
              </div>

              {/* Rooms */}
              <div className="divide-y divide-zinc-100">
                {(q.rooms || []).map((room, idx) => {
                  const pieces = room.cut_pieces || [];
                  const roomDone = pieces.every((_, pi) =>
                    qChecks.has(`${room.id}:${pi}`)
                  );
                  return (
                    <div
                      key={room.id}
                      className={`p-4 md:p-5 ${roomDone ? "bg-green-50/40" : ""}`}
                    >
                      <div className="flex items-baseline gap-3 mb-1">
                        <span className="font-mono text-[10px] text-zinc-400">
                          {String(idx + 1).padStart(2, "0")}
                        </span>
                        <h3 className="font-display text-lg font-black tracking-tighter">
                          {room.name}
                        </h3>
                        <span className="font-mono text-xs text-zinc-500 ml-auto">
                          {room.width_mm} × {room.height_mm}
                        </span>
                      </div>
                      <div
                        className="flex items-center gap-2 mb-3 ml-7"
                        data-testid={`workshop-day-room-type-${qi}-${idx}`}
                      >
                        {room.screen_type ? (
                          <span className="inline-flex items-center px-2 py-1 bg-[#09090b] text-white font-mono text-[10px] font-bold tracking-[0.18em] uppercase">
                            {typeLabel[room.screen_type] || room.screen_type}
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2 py-1 bg-amber-100 text-amber-900 border border-amber-300 font-mono text-[10px] font-bold tracking-[0.18em] uppercase">
                            ⚠ No screen type
                          </span>
                        )}
                        {room.color_detail ? (
                          <span className="font-mono text-[10px] text-zinc-500 uppercase tracking-[0.12em]">
                            · {room.color_detail}
                          </span>
                        ) : null}
                      </div>

                      {pieces.length === 0 ? (
                        <div className="font-mono text-xs text-zinc-400 italic">
                          No cut pieces.
                        </div>
                      ) : (
                        <ul className="divide-y divide-zinc-100">
                          {pieces.map((p, pi) => {
                            const key = `${room.id}:${pi}`;
                            const done = qChecks.has(key);
                            const shape = (room.piece_shapes || {})[p.label] || "";
                            return (
                              <li key={key}>
                                <button
                                  type="button"
                                  onClick={() => toggle(q.id, key)}
                                  data-testid={`workshop-day-tick-${qi}-${idx}-${pi}`}
                                  className={`w-full flex items-center gap-3 py-3 text-left hover:bg-zinc-50 transition-colors ${
                                    done ? "opacity-50" : ""
                                  }`}
                                >
                                  <span
                                    className={`flex-none w-6 h-6 border-2 flex items-center justify-center ${
                                      done
                                        ? "bg-[#09090b] border-[#09090b] text-white"
                                        : "border-zinc-300"
                                    }`}
                                  >
                                    {done ? <CheckCircle2 size={14} /> : null}
                                  </span>
                                  <span className="flex-none font-mono text-sm text-zinc-500 w-8">
                                    {p.qty}×
                                  </span>
                                  <span
                                    className={`flex-1 font-mono text-base ${
                                      done ? "line-through" : ""
                                    }`}
                                  >
                                    {p.length_mm} mm
                                  </span>
                                  {shape ? (
                                    <span
                                      className="flex-none inline-flex items-center justify-center w-7 h-7 bg-orange-500 text-white font-display font-black text-sm leading-none"
                                      title={`${shape}-shape`}
                                    >
                                      {shape}
                                    </span>
                                  ) : null}
                                  <span className="flex-none text-[10px] uppercase tracking-[0.12em] font-semibold text-zinc-500">
                                    {p.label}
                                  </span>
                                </button>
                              </li>
                            );
                          })}
                        </ul>
                      )}
                      {room.sketch_data_url ? (
                        <div className="mt-3 border border-zinc-200">
                          <div className="px-3 py-1 bg-zinc-50 border-b border-zinc-200 font-mono text-[10px] uppercase tracking-[0.18em] font-bold text-zinc-600">
                            Sketch / Notes
                          </div>
                          <img
                            src={room.sketch_data_url}
                            alt={`Sketch for ${room.name}`}
                            className="w-full block bg-white"
                          />
                        </div>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            </section>
          );
        })}
      </main>

      <footer className="max-w-4xl mx-auto p-5 md:p-8 border-t border-zinc-200 mt-8">
        <div className="font-mono text-[11px] text-zinc-400 leading-relaxed flex items-center gap-2">
          <RotateCcw size={11} />
          Changes save automatically. Cut list only — no prices shown.
        </div>
      </footer>
    </div>
  );
};

export default WorkshopDay;
